
import React, { createContext, useContext, useEffect, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useWaitlistProcessor } from "@/hooks/use-waitlist-processor";
import { useNavigate, useLocation } from "react-router-dom";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  setUser?: React.Dispatch<React.SetStateAction<User | null>>;
  setSession?: React.Dispatch<React.SetStateAction<Session | null>>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({ 
  user: null, 
  session: null,
  isLoading: true,
  signOut: async () => {}
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { processStoredWaitlistData } = useWaitlistProcessor();
  const navigate = useNavigate();
  const location = useLocation();

  // Comprehensive sign out function to clear all auth state
  const signOut = async () => {
    try {
      // First attempt a global signout
      await supabase.auth.signOut({ scope: 'global' }).catch(() => {
        // If global signout fails, try local signout
        return supabase.auth.signOut();
      });
      
      // Force clear session state
      setUser(null);
      setSession(null);
      
      // Clear any session data from localStorage
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('sb-vjthmlybhiphyoiipqjh-auth-token');
      
      // Clear any other auth-related local storage items
      const authKeys = Object.keys(localStorage).filter(key => 
        key.includes('supabase') || key.includes('sb-') || key.includes('auth')
      );
      
      authKeys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      toast.success("You've been signed out");
      
      // Redirect to home page
      window.location.href = '/';
    } catch (error) {
      console.error("Error signing out:", error);
      
      // Even if there was an error, clear the session state
      setUser(null);
      setSession(null);
      
      toast.success("You've been signed out");
      
      // Redirect to home page even on error
      window.location.href = '/';
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state changed:", event, session?.user?.email);
        
        // Fix the TypeScript error by using appropriate type comparison
        // Using a type-safe approach with a switch statement
        switch (event) {
          case 'SIGNED_OUT':
            // When signed out event happens, clear all state
            setSession(null);
            setUser(null);
            setIsLoading(false);
            
            // Clear session storage items
            sessionStorage.removeItem('waitlistFormData');
            sessionStorage.removeItem('justCompletedAuth');
            sessionStorage.removeItem('justClaimedNursingHome');
            
            // If currently on a protected route, redirect to login
            const protectedRoutes = ['/dashboard', '/owner/'];
            if (protectedRoutes.some(route => location.pathname.startsWith(route))) {
              navigate('/auth/login');
            }
            break;
            
          case 'SIGNED_IN':
          case 'USER_UPDATED':
          case 'TOKEN_REFRESHED':
            // These are the events that indicate a successful sign in or user update
            if (session) {
              setSession(session);
              setUser(session.user ?? null);
              setIsLoading(false);
              
              // Set flag for waitlist flow
              if (!sessionStorage.getItem('justCompletedAuth')) {
                sessionStorage.setItem('justCompletedAuth', 'true');
              }
              
              toast.success(`Welcome${session.user.user_metadata?.first_name ? ' ' + session.user.user_metadata.first_name : ''}!`);
              
              // Check if this is a nursing home owner who just claimed a profile
              const justClaimedNH = sessionStorage.getItem('justClaimedNursingHome');
              
              if (justClaimedNH) {
                // Clear the session storage flag
                sessionStorage.removeItem('justClaimedNursingHome');
                
                // Redirect to owner dashboard
                if (!location.pathname.includes('/owner/')) {
                  navigate('/owner/dashboard');
                  return;
                }
              }
              
              // Process waitlist data if needed - but don't process on auth page to avoid redirect loops
              if (!location.pathname.includes('/auth/')) {
                await processStoredWaitlistData(session);
              }
            }
            break;
            
          default:
            // For other events like INITIAL_SESSION, PASSWORD_RECOVERY, etc.
            if (session) {
              setSession(session);
              setUser(session.user ?? null);
            } else {
              setSession(null);
              setUser(null);
            }
            setIsLoading(false);
            break;
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [navigate, location.pathname]);

  const value = {
    user,
    session,
    isLoading,
    setUser,
    setSession,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
